import React, { useState, useEffect, useContext } from 'react'
import { UserContext } from '../../../context/UserContext'
import CustomModal from '../../Elements/CustomModal'
import { DEFAULT_VENUE_TYPE, VENUE_TYPE_OPTIONS } from '../../../lib/consts/venueTypes'
import { getImageCacheKey, invalidateImageCache } from '../../../lib/helpers'
// Los planes salen del catálogo (`src/lib/planes.js`), no de una lista a mano:
// esta era la CUARTA copia y se quedó con los cuatro tramos viejos mientras el
// resto de la interfaz ya ofrecía Prueba, Club y Club Pro.
import { SELECTABLE_PLANS as PLAN_OPTIONS, PLAN_LABELS } from '../../../lib/shopBilling'



export default function ShopsModalAdv({ active, handleModal, token, id, setErrorMessage }) {
    const [name, setName] = useState("");
    const [, setSubdomain] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState("");
    const [taxId, setTaxId] = useState("");
    const [description, setDescription] = useState("");
    const [venueType, setVenueType] = useState(DEFAULT_VENUE_TYPE);
    const [planTier, setPlanTier] = useState("basico");
    const [shop_owner, setShopOwner] = useState([]);
    const [inviteOwnerEmail, setInviteOwnerEmail] = useState("");
    const [, , , , , , , , , , refreshVenues] = useContext(UserContext);

    const [selectedShopOwners, setSelectedShopOwners] = useState([]);
    const [image, setImage] = useState(null);  // State to hold the file
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [localError, setLocalError] = useState('');
    const [activeTab, setActiveTab] = useState('datos');

    const selectedOwnerObjects = selectedShopOwners
      .map((ownerId) => shop_owner.find((owner) => String(owner.id) === String(ownerId)))
      .filter(Boolean);
    const primaryExistingOwner = selectedOwnerObjects[0] || null;
    const notificationTarget = inviteOwnerEmail.trim() || primaryExistingOwner?.email || null;

    // Fetch shop owners (solo owners, no superusers)
    useEffect(() => {
        if (!token) return;
        
        const fetchShopOwners = async () => {
            const requestOptions = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + token,
                },
            };
            try {
                // Usar endpoint específico para owners que excluye superusers
                const response = await fetch(`/v1/owners`, requestOptions);
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ detail: 'Error al obtener owners' }));
                    throw new Error(errorData.detail || "Could not fetch shop owners");
                }
                const data = await response.json();
                // El endpoint /v1/owners ya devuelve solo owners, no necesitamos filtrar
                setShopOwner(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching shop owners:", error);
                if (setErrorMessage) {
                    setErrorMessage(error.message || 'Could not get shop owners data');
                }
            }
        };
        fetchShopOwners();
    }, [token, setErrorMessage]);
    
    // Load venue data if editing
    useEffect(() => {
        // Solo hacer fetch si hay un ID válido, token y el modal está activo
        // Reset form when modal opens/closes or id changes
        if (!active) {
            setName("");
            setAddress("");
            setCity("");
            setPostalCode("");
            setPhone("");
            setEmail("");
            setWebsite("");
            setTaxId("");
            setDescription("");
            setVenueType(DEFAULT_VENUE_TYPE);
            setPlanTier("basico");
            setSubdomain("");
            setSelectedShopOwners([]);
            setInviteOwnerEmail("");
            setImage(null);
            setLocalError('');
        }
        
        if (!id || !token || !active) {
            // Si no hay ID, limpiar el formulario (modo creación)
            if (!id && active) {
                setName("");
                setAddress("");
                setCity("");
                setPostalCode("");
                setPhone("");
                setEmail("");
                setWebsite("");
                setTaxId("");
                setDescription("");
                setVenueType(DEFAULT_VENUE_TYPE);
                setPlanTier("basico");
                setSubdomain("");
                setSelectedShopOwners([]);
                setInviteOwnerEmail("");
                setImage(null);
                setLocalError('');
            }
            return;
        }
        
        const fetchVenue = async () => {
            try {
                setLocalError(''); // Limpiar errores previos
                console.log(`[ShopsModalAdv] Fetching venue with ID: ${id}`);
                const response = await fetch(`/v1/venues/${id}`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + token,
                    },
                });
                
                if (!response.ok) {
                    if (response.status === 404) {
                        const errorMsg = 'La tienda no fue encontrada o no tienes permisos para verla';
                        setLocalError(errorMsg);
                        if (setErrorMessage) {
                            setErrorMessage(errorMsg);
                        }
                        // Limpiar formulario si no se puede cargar
                        setName("");
                        setAddress("");
                        setCity("");
                        setPostalCode("");
                        setPhone("");
                        setEmail("");
                        setWebsite("");
                        setTaxId("");
                        setDescription("");
                        setSubdomain("");
                        setSelectedShopOwners([]);
                        return;
                    }
                    if (response.status === 403) {
                        const errorMsg = 'No tienes permisos para ver esta tienda';
                        setLocalError(errorMsg);
                        if (setErrorMessage) {
                            setErrorMessage(errorMsg);
                        }
                        return;
                    }
                    const errorData = await response.json().catch(() => ({ detail: 'Error al cargar la tienda' }));
                    throw new Error(errorData.detail || "Could not fetch venue data");
                }
                
                const data = await response.json();
                setName(data.name || "");
                setAddress(data.address || "");
                setCity(data.city || "");
                setPostalCode(data.postal_code || "");
                setPhone(data.phone || "");
                setEmail(data.email || "");
                setWebsite(data.website || "");
                setTaxId(data.tax_id || "");
                setDescription(data.description || "");
                setVenueType(data.venue_type || DEFAULT_VENUE_TYPE);
                setPlanTier(data.plan_tier || "basico");
                setSubdomain(data.subdomain || "");
                setInviteOwnerEmail("");
                // Set selected owners if available
                // Backend now returns owners as array of objects with id, email, and name
                if (data.owners && Array.isArray(data.owners)) {
                    // Extract owner IDs y convertir a string para que coincidan con los valores del select
                    const ownerIds = data.owners.map(o => {
                        // If it's already a UUID string (legacy format), use it directly
                        if (typeof o === 'string') {
                            return String(o);
                        }
                        // If it's an object with id property, extract the id and convert to string
                        const ownerId = o.id || o;
                        return String(ownerId); // Asegurar que sea string para comparación
                    });
                    console.log('[ShopsModalAdv] Venue owners from API:', data.owners);
                    console.log('[ShopsModalAdv] Setting selected owners (as strings):', ownerIds);
                    setSelectedShopOwners(ownerIds);
                } else {
                    setSelectedShopOwners([]);
                }
                setLocalError(''); // Limpiar errores si todo va bien
            } catch (error) {
                console.error("Error fetching venue:", error);
                const errorMsg = error.message || 'Error al cargar los datos de la tienda';
                setLocalError(errorMsg);
                if (setErrorMessage) {
                    setErrorMessage(errorMsg);
                }
            }
        };
        
        fetchVenue();
    // Sin `shop_owner` en las dependencias: en modo creación este efecto vacía el
    // formulario entero, y al resolver /v1/owners se re-disparaba y borraba lo que el
    // usuario ya había escrito. Aquí solo importan la tienda que se edita y la sesión.
    }, [id, token, active, setErrorMessage]);


    const submitRegistration = async () => {
      setLocalError('');
      setIsSubmitting(true);
      
      // Validaciones
      if (!name || name.trim() === '') {
        setActiveTab('datos');
        setLocalError('El nombre de la tienda es requerido');
        setIsSubmitting(false);
        return;
      }
      
      if (!address || address.trim() === '') {
        setActiveTab('contacto');
        setLocalError('La dirección es requerida');
        setIsSubmitting(false);
        return;
      }
      
      // Build the new venue data object
      const newVenueData = {
        name: name.trim(),
        address: address.trim(),
        city: city.trim(),
        postal_code: postalCode.trim(),
        phone: phone.trim(),
        email: email.trim(),
        website: website.trim(),
        tax_id: taxId.trim(),
        description: description.trim(),
        venue_type: venueType,
        plan_tier: planTier,
        shop_owner: selectedShopOwners,
        invite_owner_email: inviteOwnerEmail.trim(),
      };
    
      const formData = new FormData();
      // Use 'new_venue' for POST and 'venue_data' for PUT
      const venueFieldName = id ? 'venue_data' : 'new_venue';
      formData.append(venueFieldName, JSON.stringify(newVenueData));
      if (image) {
        formData.append('image', image);
      }
    
      const requestOptions = {
        method: id ? 'PUT' : 'POST',
        headers: { 
          Authorization: "Bearer " + token
        },
        body: formData,
      };
    
      try {
        const endpoint = id ? `/v1/venues/${id}` : '/v1/venues';
        const response = await fetch(endpoint, requestOptions);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: 'Error al procesar la solicitud' }));
          throw errorData;
        }
        
        const data = await response.json();
        invalidateImageCache(getImageCacheKey('venue', data.id || id));
        console.log('[ShopsModalAdv] Response after update:', data);
        console.log('[ShopsModalAdv] Owners in response:', data.owners);
        
        // Mostrar mensaje de éxito
        const invitedEmails = inviteOwnerEmail
          .split(/[\n,;]+/)
          .map((value) => value.trim())
          .filter(Boolean);
        const invitedSummary = invitedEmails.length > 0
          ? `. Se han procesado las invitaciones para: ${invitedEmails.join(', ')}.`
          : '';
        const successMessage = id 
          ? `Tienda "${data.name}" actualizada con éxito${invitedSummary}`
          : `Tienda "${data.name}" creada con éxito${invitedSummary}`;
        alert(successMessage);
        
        // Si estamos editando, actualizar el estado con los nuevos datos del servidor
        if (id) {
            setName(data.name || "");
            setAddress(data.address || "");
            setCity(data.city || "");
            setPostalCode(data.postal_code || "");
            setPhone(data.phone || "");
            setEmail(data.email || "");
            setWebsite(data.website || "");
            setTaxId(data.tax_id || "");
            setDescription(data.description || "");
            setVenueType(data.venue_type || DEFAULT_VENUE_TYPE);
            setPlanTier(data.plan_tier || "basico");
            setSubdomain(data.subdomain || "");
            // Actualizar los owners seleccionados con los datos del servidor
            if (data.owners && Array.isArray(data.owners)) {
                const ownerIds = data.owners.map(o => {
                    if (typeof o === 'string') {
                        return String(o);
                    }
                    return String(o.id || o);
                });
                console.log('[ShopsModalAdv] Updated owners from server:', ownerIds);
                setSelectedShopOwners(ownerIds);
            } else {
                setSelectedShopOwners([]);
            }
        } else {
            // Si estamos creando, limpiar formulario
            setName("");
            setAddress("");
            setCity("");
            setPostalCode("");
            setPhone("");
            setEmail("");
            setWebsite("");
            setTaxId("");
            setDescription("");
            setVenueType(DEFAULT_VENUE_TYPE);
            setPlanTier("basico");
            setSubdomain("");
            setSelectedShopOwners([]);
            setInviteOwnerEmail("");
        }
        setImage(null);
        setLocalError('');
        
        // Refrescar los venues en el contexto para actualizar el icono
        if (refreshVenues) {
          await refreshVenues();
        }
        
        // Cerrar modal - el componente padre debería recargar
        handleModal();
      } catch (error) {
        console.error("Error creating/updating venue:", error);
        
        // Handle different error formats from FastAPI
        let errorMsg = 'An error occurred during registration.';
        
        if (error.detail) {
          // If detail is an array (validation errors), format them
          if (Array.isArray(error.detail)) {
            errorMsg = error.detail
              .map(err => {
                const field = err.loc ? err.loc.join('.') : 'field';
                return `${field}: ${err.msg || 'Invalid value'}`;
              })
              .join('; ');
          } else if (typeof error.detail === 'string') {
            // If detail is a string, use it directly
            errorMsg = error.detail;
          } else if (typeof error.detail === 'object') {
            // If detail is an object, try to stringify it
            errorMsg = JSON.stringify(error.detail);
          }
        } else if (error.message) {
          errorMsg = error.message;
        }
        
        setLocalError(errorMsg);
        if (setErrorMessage) {
          setErrorMessage(errorMsg);
        }
      } finally {
        setIsSubmitting(false);
      }
    };
    

    const handleSubmit = (e) => {
        e.preventDefault();
        // const shopOwnerIdsArray = shop_owner.split(',').map(id => id.trim());
        submitRegistration();
    };

    const handleToggleShopOwner = (ownerId) => {
        setSelectedShopOwners((currentOwners) => {
            if (currentOwners.includes(ownerId)) {
                return currentOwners.filter((id) => id !== ownerId);
            }
            return [...currentOwners, ownerId];
        });
    };
    

    const handleImageChange = (e) => {
        setImage(e.target.files[0]); // Set the file into state
      };


    // Pestañas: el formulario tiene ~12 campos más el selector de propietarios y la
    // invitación por email. En un solo scroll era difícil saber qué faltaba. Los campos
    // siguen todos montados (se ocultan con `hidden`), así que el submit y la validación
    // nativa del navegador funcionan igual estés en la pestaña que estés.
    const TABS = [
      { key: 'datos', label: 'Datos' },
      { key: 'contacto', label: 'Ubicación y contacto' },
      { key: 'imagen', label: 'Imagen y descripción' },
      { key: 'propietarios', label: 'Propietarios' },
    ];

    const modalContent = (
        <form onSubmit={handleSubmit} noValidate className="space-y-6 p-4">
          {/* noValidate a propósito: los campos obligatorios (nombre, dirección) viven en
              pestañas distintas y cada pestaña inactiva es un <div hidden>. La validación
              nativa no puede enfocar un campo en display:none, así que el navegador abortaba
              el submit en silencio ("An invalid form control ... is not focusable"):
              handleSubmit no llegaba a ejecutarse, no se creaba la tienda y no se veía ningún
              aviso. Valida submitRegistration, que además abre la pestaña del campo que falta. */}
          <div className="flex flex-wrap gap-1 border-b border-gray-200" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                onClick={() => setActiveTab(t.key)}
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                  activeTab === t.key
                    ? 'border-emerald-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {localError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{localError}</span>
            </div>
          )}

          {notificationTarget && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <strong className="font-semibold">Owner que recibirá el email:</strong> {notificationTarget}
              <p className="mt-1 text-xs text-amber-800">
                Si escribes varios emails, se invitará a todos. Los owners existentes que selecciones también recibirán el correo de asignación de tienda.
              </p>
            </div>
          )}
          
          <div hidden={activeTab !== 'datos'} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Nombre del establecimiento</label>
            <input
              type="name"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
              required
            />
          </div>

          <div>
            <label htmlFor="venue_type" className="block text-sm font-medium text-gray-700">Tipo de venue</label>
            <select
              id="venue_type"
              value={venueType}
              onChange={(e) => setVenueType(e.target.value)}
              className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
            >
              {VENUE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="plan_tier" className="block text-sm font-medium text-gray-700">Paquete inicial</label>
            <select
              id="plan_tier"
              value={planTier}
              onChange={(e) => setPlanTier(e.target.value)}
              className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
            >
              {/* Si el club está en uno de los planes viejos (Kong va en Premium), ese
                  plan NO está en la lista de asignables. Sin esta opción, el select se
                  queda sin nada que casar: enseña el primero de la lista y guardar le
                  cambia el plan sin que nadie lo haya pedido. Se añade la suya, marcada
                  como actual y deshabilitada — se ve, no se vuelve a poner. */}
              {!PLAN_OPTIONS.some((o) => o.value === planTier) && planTier && (
                <option value={planTier} disabled>
                  {PLAN_LABELS[planTier] || planTier} (plan actual)
                </option>
              )}
              {PLAN_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Este plan se usará como paquete inicial de la tienda y como opción por defecto en el flujo de Stripe.
            </p>
          </div>
          </div>

          <div hidden={activeTab !== 'contacto'} className="space-y-6">
            <div className="relative">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700">Dirección del establecimiento</label>
            <input
              type="text"
              id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
                    required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700">Ciudad</label>
              <input
                type="text"
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
              />
            </div>
            <div>
              <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700">Código postal</label>
              <input
                type="text"
                id="postal_code"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Teléfono</label>
              <input
                type="text"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700">Web</label>
              <input
                type="text"
                id="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
                placeholder="https://..."
              />
            </div>
            <div>
              <label htmlFor="tax_id" className="block text-sm font-medium text-gray-700">CIF / NIF</label>
              <input
                type="text"
                id="tax_id"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
              />
            </div>
          </div>
          </div>

          <div hidden={activeTab !== 'imagen'} className="space-y-6">
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">Descripción</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="3"
              className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
            />
          </div>


            <div>
                <label htmlFor="image" className="block text-sm font-medium text-gray-700">
                  Subir imagen <span className="text-gray-400 text-xs font-normal">(opcional)</span>
                </label>
                <input
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageChange}
                className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
                />
            </div>
          </div>

          <div hidden={activeTab !== 'propietarios'} className="space-y-6">
            <div>
                <label htmlFor="shop_owner" className="block text-sm font-medium text-gray-700 mb-1">
                  Propietarios de la Tienda
                  <span className="text-gray-400 text-xs font-normal ml-1">(puedes seleccionar varios)</span>
                </label>
                {selectedOwnerObjects.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {selectedOwnerObjects.map((owner) => {
                      const ownerId = String(owner.id);
                      const firstName = owner.profile?.first_name || '';
                      const lastName = owner.profile?.last_name || '';
                      const fullName = `${firstName} ${lastName}`.trim();
                      const displayName = fullName || owner.email || ownerId;

                      return (
                        <span
                          key={`selected-${ownerId}`}
                          className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-700"
                        >
                          {displayName}
                        </span>
                      );
                    })}
                  </div>
                )}
                <div
                  id="shop_owner"
                  className="mt-1 max-h-56 space-y-2 overflow-y-auto rounded-lg border border-gray-300 bg-white p-3"
                >
                  {shop_owner && shop_owner.length > 0 ? (
                    shop_owner.map((owner) => {
                      const ownerId = String(owner.id);
                      const firstName = owner.profile?.first_name || '';
                      const lastName = owner.profile?.last_name || '';
                      const fullName = `${firstName} ${lastName}`.trim();
                      const displayName = fullName || owner.email || ownerId;
                      const isSelected = selectedShopOwners.includes(ownerId);

                      return (
                        <label
                          key={ownerId}
                          className={`flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 transition ${
                            isSelected
                              ? 'border-purple-300 bg-purple-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleShopOwner(ownerId)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900">{displayName}</div>
                            {owner.email && displayName !== owner.email && (
                              <div className="text-xs text-gray-500">{owner.email}</div>
                            )}
                          </div>
                        </label>
                      );
                    })
                  ) : (
                    <div className="text-sm text-gray-500">No hay propietarios disponibles</div>
                  )}
                </div>
            </div>

              <div>
                  <label htmlFor="invite_owner_email" className="block text-sm font-medium text-gray-700 mb-1">
                    Invitar nuevos owners por email
                  </label>
                  <textarea
                    id="invite_owner_email"
                    value={inviteOwnerEmail}
                    onChange={(e) => setInviteOwnerEmail(e.target.value)}
                    className="mt-1 px-3 py-2 bg-white border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full"
                    placeholder={"owner1@club.com, owner2@club.com\nowner3@club.com"}
                    rows="3"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Opcional. Puedes escribir uno o varios emails separados por comas o saltos de línea. ClubsAgent creará esos owners, los vinculará a esta tienda y enviará su email de acceso si SMTP está configurado.
                  </p>
              </div>

          
            </div>

            <div className="flex items-center justify-between">
                <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm ${
                  isSubmitting 
                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                >
                {isSubmitting ? 'Guardando...' : (id ? 'Actualizar Tienda' : 'Crear Tienda')}
                </button>
            </div>
        </form>
    );

    // Reset form when modal closes
    useEffect(() => {
        if (!active) {
            // Limpiar formulario cuando el modal se cierra
            setName("");
            setAddress("");
            setVenueType(DEFAULT_VENUE_TYPE);
            setPlanTier("basico");
            setSubdomain("");
            setSelectedShopOwners([]);
            setInviteOwnerEmail("");
            setImage(null);
            setLocalError('');
            setActiveTab('datos');
        }
    }, [active]);

    return (
      <CustomModal
        active={active}
        handleModal={handleModal}
        title={id ? 'Actualizar Tienda' : 'Crear Tienda'}
        children={modalContent}
      />
    )
}
